import { TireSeason, TireSeasonLabels, type RouteItemCargoExpectationRecord } from '@navio/nsdk';
import type { NoddiAsyncError } from 'noddi-types';
import {
  ApiErrorMessageInternal,
  NoddiButton,
  NoddiDialog,
  NoddiSelectionCardGroup,
  type NoddiSelectionCardOption
} from 'noddi-ui-web';
import { reloadLabels } from 'noddi-util';
import { useState } from 'react';
import { TIRE_SEASON_STYLE } from '../../CarWheel/tireSeasonStyle';
import { CargoExpectationCustomerWheelsetBlock } from './CargoExpectationRowCard';

// A wheel set's assumed season is corrected to summer or winter; all-season is never assumed.
const SEASON_OPTIONS: NoddiSelectionCardOption<TireSeason>[] = [TireSeason.SUMMER, TireSeason.WINTER].map((season) => ({
  value: season,
  label: TireSeasonLabels[season],
  icon: TIRE_SEASON_STYLE[season].icon,
  iconWrapClassName: TIRE_SEASON_STYLE[season].tintClassName
}));

type Props = {
  row: RouteItemCargoExpectationRecord;
  customerName: string;
  routeItemId: number;
  onDismiss: () => void;
  onSuccess: () => void | Promise<void>;
  postTireSeason: (args: { routeItemId: number; expectationIds: number[]; tireSeason: TireSeason }) => Promise<void>;
  isTireSeasonPending: boolean;
};

export const SeasonActionDialog = ({
  row,
  customerName,
  routeItemId,
  onDismiss,
  onSuccess,
  postTireSeason,
  isTireSeasonPending
}: Props) => {
  const tireSeason = row.storageObjectGroup.carWheelSet?.tireSeason?.value ?? row.tireSeasonOverride ?? null;
  const [selectedSeason, setSelectedSeason] = useState<TireSeason>(
    tireSeason === TireSeason.WINTER ? TireSeason.WINTER : TireSeason.SUMMER
  );
  const [submitError, setSubmitError] = useState<NoddiAsyncError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await postTireSeason({ routeItemId, expectationIds: [row.id], tireSeason: selectedSeason });
      await onSuccess();
      onDismiss();
    } catch (error) {
      setSubmitError(error as NoddiAsyncError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBusy = isSubmitting || isTireSeasonPending;

  return (
    <NoddiDialog
      open
      onClose={onDismiss}
      title={reloadLabels.actionChangeTireSeason}
      description='Select the correct tire season for these wheels.'
      footer={
        <div className='flex justify-end gap-2'>
          <NoddiButton variant='outline' onClick={onDismiss} disabled={isBusy}>
            Cancel
          </NoddiButton>
          <NoddiButton onClick={() => void handleSubmit()} disabled={isBusy} loading={isBusy}>
            Save
          </NoddiButton>
        </div>
      }
    >
      <div className='flex flex-col gap-4'>
        {submitError != null && <ApiErrorMessageInternal error={submitError} />}
        <CargoExpectationCustomerWheelsetBlock row={row} customerName={customerName} />
        <NoddiSelectionCardGroup
          title='Season'
          size='small'
          options={SEASON_OPTIONS}
          value={selectedSeason}
          onValueChange={setSelectedSeason}
        />
      </div>
    </NoddiDialog>
  );
};
